<template>
    <div>
        <CoreTwoColumnsRow>
            <template #left>
                <CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-45">
                    <span>{{ group }}.{{ keyName }}</span>
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    :modelValue="fullKey"
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    :disabled="true"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow v-for="locale in locales" :key="locale">
            <template #left>
                <CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-45">
                    <span>{{ locale }}</span>
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    v-model="values[locale]"
                    class="flex-grow-1 flex-shrink-1 flex-basis-55"
                    placeholder="§translation_editor.placeholder_translation"
                    :disabled="saving"
                />
            </template>
        </CoreTwoColumnsRow>

        <div class="mt-4 flex justify-start gap-2">
            <CoreButton
                label="§ui_button.save"
                icon="pi-check"
                :loading="saving"
                :disabled="saving || !hasAnyValue"
                @click="handleSave"
            />

            <CoreButton
                label="§translation_editor.open_full_editor"
                icon="pi-external-link"
                variant="tertiary"
                :disabled="saving"
                @click="handleOpenFullEditor"
            />

            <CoreButton
                label="§ui_button.cancel"
                icon="pi-times"
                variant="tertiary"
                :disabled="saving"
                @click="handleClose"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal'
import TranslationManagerService from '@/api/TranslationManagerService'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'

const SUPPORTED_LOCALES = ['en-GB', 'en-US', 'de-DE']

export default {
    name: 'TranslationEditorModal',

    components: {
        CoreTwoColumnsRow,
        CoreBorderWrapper,
        CoreStringInput,
        CoreButton,
    },

    data() {
        return {
            modalStore: useModalStore(),
            locales: SUPPORTED_LOCALES,
            group: '',
            keyName: '',
            fullKey: '',
            values: Object.fromEntries(SUPPORTED_LOCALES.map((l) => [l, ''])),
            saving: false,
        }
    },

    computed: {
        hasAnyValue() {
            return this.locales.some((locale) => (this.values[locale] || '').trim() !== '')
        },
    },

    mounted() {
        const data = this.modalStore.data || {}
        this.group = data.group || ''
        this.keyName = data.key || ''
        this.fullKey = data.fullKey || `${this.group}.${this.keyName}`
    },

    methods: {
        handleClose() {
            this.modalStore.setClose()
        },

        handleOpenFullEditor() {
            const url = this.group ? `/translations/${this.group}` : '/translations'
            window.open(url, '_blank', 'noopener')
        },

        async handleSave() {
            if (!this.group || !this.keyName) return

            this.saving = true
            try {
                const values = Object.fromEntries(
                    this.locales.map((locale) => [locale, (this.values[locale] || '').trim()])
                )

                await TranslationManagerService.saveTranslation({
                    group: this.group,
                    key: this.keyName,
                    values,
                })

                const message = this.$t('§translation_editor.saved_reload_required')
                this.modalStore.toast.add({
                    severity: 'success',
                    summary: this.$t('§translation_editor.saved_title'),
                    detail: message,
                    life: this.modalStore.getToastDuration(message, 'success'),
                })

                this.modalStore.setClose()
            } catch (error) {
                console.error('Failed to save translation:', error)
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
